import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { dynastyTheme } from '../../../../../services/colorService';

const C = dynastyTheme.tokens.colors;

/**
 * PlayerPhoto Component
 * 
 * Displays player photo from MLB Static API with fallback to generic image.
 * Handles loading states and errors gracefully.
 * 
 * @param {number} mlbId - MLB player ID
 * @param {string} size - Photo size: "40x40" | "50x50" | "60x60" | "80x80" | "100x100" | "120x120" | "200x200"
 * @param {boolean} fallback - Whether to show fallback image on error (default: true)
 * @param {string} className - Additional CSS classes
 */
const PlayerPhoto = ({ mlbId, size = '80x80', fallback = true, className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Parse size string to get dimensions
  const [width, height] = size.split('x').map(Number);

  // Ensure mlbId is a number (handle string IDs)
  const numericMlbId = mlbId ? Number(mlbId) : null;

  // MLB Static API URL pattern (from Phase 1A analysis)
  // Use same pattern as desktop PlayerInfoCard
  const photoUrl = numericMlbId 
    ? `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_${width},h_${height},c_fill,g_auto/v1/people/${numericMlbId}/headshot/silo/current`
    : null;

  // Fallback generic player image
  const fallbackUrl = `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_${width},h_${height},c_fill,g_auto/v1/people/0/headshot/silo/current`;

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    if (fallback) {
      setImageError(true);
    }
  };

  const containerStyle = {
    width: `${width}px`,
    height: `${height}px`,
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: C.surface[855], // dynastyTheme.colors.darkLighter
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative'
  };

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: isLoading ? 'none' : 'block'
  };

  const loaderStyle = {
    width: '60%',
    height: '60%',
    borderRadius: '50%',
    backgroundColor: C.coolGray[850],
    animation: 'pulse 1.5s ease-in-out infinite'
  };

  // If no mlbId, show fallback immediately
  const imageSrc = !numericMlbId ? fallbackUrl : (imageError ? fallbackUrl : photoUrl);

  return (
    <div style={containerStyle} className={className}>
      {isLoading && <div style={loaderStyle} />}
      <img
        src={imageSrc}
        alt={numericMlbId ? `Player ${numericMlbId}` : 'Player'}
        style={imageStyle}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

PlayerPhoto.propTypes = {
  mlbId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  size: PropTypes.oneOf([
    '40x40',
    '50x50',
    '60x60',
    '80x80',
    '100x100',
    '120x120',
    '200x200'
  ]),
  fallback: PropTypes.bool,
  className: PropTypes.string
};

export default PlayerPhoto;
